import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";

// Suppress CKEditor telemetry errors that trigger the dev overlay.
const originalConsoleError = console.error;
console.error = (...args: unknown[]) => {
  const hasTelemetryError = args.some((arg) => {
    if (!arg) return false;
    const message = arg instanceof Error
      ? arg.message
      : typeof arg === "object" && "message" in (arg as { message?: unknown })
        ? String((arg as { message?: unknown }).message)
        : String(arg);
    const lower = message.toLowerCase();
    return lower.includes("send events batch") || lower.includes("forcing send events");
  });

  if (hasTelemetryError) {
    return;
  }

  originalConsoleError(...args);
};

// Override window.onerror to suppress CKEditor telemetry errors
const originalOnError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  const msg = typeof message === 'string' ? message.toLowerCase() : '';
  const errorMessage = error?.message ? error.message.toLowerCase() : '';
  if (msg.includes('send events batch') ||
      errorMessage.includes('send events batch') ||
      msg.includes('forcing send events') ||
      errorMessage.includes('forcing send events')) {
    console.log('Suppressed CKEditor telemetry error');
    return true; // Prevent default error handling
  }
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

// Global error handler to suppress CKEditor telemetry errors
window.addEventListener('error', (event) => {
  const message = event.message ? event.message.toLowerCase() : '';
  const errorMessage = event.error?.message ? event.error.message.toLowerCase() : '';
  if (message.includes('send events batch') ||
      message.includes('forcing send events') ||
      errorMessage.includes('send events batch') ||
      errorMessage.includes('forcing send events')) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    console.log('Suppressed CKEditor telemetry error via event listener');
    return false;
  }
}, true); // Use capture phase

// Suppress unhandled promise rejections from CKEditor telemetry
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = typeof reason === 'string'
    ? reason.toLowerCase()
    : reason?.message ? String(reason.message).toLowerCase() : '';
  if (message.includes('send events batch') ||
      message.includes('forcing send events')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    console.log('Suppressed CKEditor telemetry promise rejection');
  }
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
